import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { AdminSidebarLayout } from './SidebarLayout';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Badge } from './ui/badge';
import { Loader2, RefreshCw, Sparkles, AlertTriangle, CheckCircle, Lock, LockOpen, Plus, Trash2, Activity } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Admin → Marketing Autopilot (Phase 1)
 * Read-only view over Business Health + Strategy Engine + Approved Claims
 * library + engine config. Publishing + AI generation land in Phase 2/3.
 */
export function AdminMarketingAutopilotPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [health, setHealth] = useState(null);
  const [strategy, setStrategy] = useState(null);
  const [config, setConfig] = useState(null);
  const [claims, setClaims] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [tab, setTab] = useState('strategy');
  const [savingConfig, setSavingConfig] = useState(false);
  const [claimForm, setClaimForm] = useState({ text: '', source: '', topic: 'general', qualifications: '', valid_until: '' });
  const [addingClaim, setAddingClaim] = useState(false);
  const [lockForm, setLockForm] = useState({ objective: '', days: 7 });
  const [campaigns, setCampaigns] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [regeneratingId, setRegeneratingId] = useState(null);
  const [brokenImgs, setBrokenImgs] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [liveMode, setLiveMode] = useState({});
  const [accountForm, setAccountForm] = useState({
    network: 'facebook_page', external_account_id: '', display_name: '',
    access_token: '', language: 'en', enabled: true,
  });
  const [savingAccount, setSavingAccount] = useState(false);
  const [publishingId, setPublishingId] = useState(null);

  const fetchAccounts = useCallback(async () => {
    try {
      const { data } = await api.get('/api/admin/marketing/accounts');
      setAccounts(data.accounts || []);
      setLiveMode(data.live_mode || {});
    } catch { /* non-fatal */ }
  }, [api]);

  const fetchCampaigns = useCallback(async () => {
    try {
      const { data } = await api.get('/api/admin/marketing/campaigns?limit=50');
      setCampaigns(data.campaigns || []);
    } catch { /* non-fatal — tab shows empty state */ }
  }, [api]);

  const fetchAll = useCallback(async () => {
    setRefreshing(true);
    try {
      const [s, c, cl, ob] = await Promise.all([
        api.get('/api/admin/marketing/strategy'),
        api.get('/api/admin/marketing/config'),
        api.get('/api/admin/marketing/claims'),
        api.get('/api/admin/marketing/objectives'),
      ]);
      setStrategy(s.data.strategy);
      setHealth(s.data.health);
      setConfig(c.data);
      setClaims(cl.data.claims || []);
      setObjectives(ob.data.objectives || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load marketing data');
    }
    setRefreshing(false);
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);
  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const addAccount = async () => {
    if (!accountForm.external_account_id.trim() || !accountForm.display_name.trim()) {
      toast.error('Page/Account ID and display name are required');
      return;
    }
    if (accountForm.network !== 'mock' && !accountForm.access_token.trim()) {
      toast.error('Access token is required for real networks');
      return;
    }
    setSavingAccount(true);
    try {
      await api.post('/api/admin/marketing/accounts', accountForm);
      toast.success('Account connected');
      setAccountForm({ ...accountForm, external_account_id: '', display_name: '', access_token: '' });
      fetchAccounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Connect failed');
    }
    setSavingAccount(false);
  };

  const toggleAccount = async (id, enabled) => {
    try {
      await api.patch(`/api/admin/marketing/accounts/${id}`, { enabled });
      fetchAccounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const deleteAccount = async (id) => {
    if (!window.confirm('Disconnect this account?')) return;
    try {
      await api.delete(`/api/admin/marketing/accounts/${id}`);
      setAccounts(accounts.filter((a) => a.id !== id));
      toast.success('Account disconnected');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Disconnect failed');
    }
  };

  const publishCampaign = async (campaignId) => {
    if (accounts.filter((a) => a.enabled).length === 0) {
      toast.error('No enabled accounts. Connect one first.');
      return;
    }
    setPublishingId(campaignId);
    try {
      const { data } = await api.post(`/api/admin/marketing/campaigns/${campaignId}/publish`, {});
      const mockCount = (data.results || []).filter((r) => r.mock).length;
      const okCount = (data.results || []).filter((r) => r.status === 'published').length;
      const dupCount = (data.results || []).filter((r) => r.status === 'skipped_duplicate').length;
      if (okCount > 0 && mockCount > 0) {
        toast.warning(`Published to ${okCount} account(s) in MOCK mode. Configure META_APP_ID + META_APP_SECRET to go live.`);
      } else if (okCount > 0) {
        toast.success(`Published to ${okCount} account(s) LIVE`);
      } else if (dupCount > 0) {
        toast.info(`All ${dupCount} account(s) already had this post`);
      } else {
        toast.error(`Publish failed: ${data.results?.[0]?.error || 'unknown'}`);
      }
      fetchCampaigns();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Publish failed');
    }
    setPublishingId(null);
  };

  const generateDraft = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post('/api/admin/marketing/campaigns/generate', { test_only: true });
      if (data.status === 'skipped') {
        toast.warning(`Skipped: ${data.reason}`);
      } else {
        toast.success('Draft generated');
      }
      fetchCampaigns();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    }
    setGenerating(false);
  };

  const regeneratePart = async (campaignId, part) => {
    setRegeneratingId(campaignId);
    try {
      await api.post(`/api/admin/marketing/campaigns/${campaignId}/regenerate?part=${part}`);
      toast.success(`${part} regenerated`);
      fetchCampaigns();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Regenerate failed');
    }
    setRegeneratingId(null);
  };

  const deleteCampaign = async (campaignId) => {
    if (!window.confirm('Delete this draft?')) return;
    try {
      await api.delete(`/api/admin/marketing/campaigns/${campaignId}`);
      setCampaigns(campaigns.filter((c) => c.id !== campaignId));
      toast.success('Campaign deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const saveConfig = async (patch) => {
    setSavingConfig(true);
    try {
      const { data } = await api.put('/api/admin/marketing/config', patch);
      setConfig(data);
      toast.success('Settings saved');
      // Recompute strategy right away so the UI reflects the new rules.
      const s = await api.get('/api/admin/marketing/strategy');
      setStrategy(s.data.strategy);
      setHealth(s.data.health);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSavingConfig(false);
  };

  const addClaim = async () => {
    if (!claimForm.text.trim() || !claimForm.source.trim()) {
      toast.error('Text and source are required');
      return;
    }
    setAddingClaim(true);
    try {
      const { data } = await api.post('/api/admin/marketing/claims', claimForm);
      setClaims([data, ...claims]);
      setClaimForm({ text: '', source: '', topic: 'general', qualifications: '', valid_until: '' });
      toast.success('Claim added');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add claim');
    }
    setAddingClaim(false);
  };

  const deleteClaim = async (id) => {
    if (!window.confirm('Delete this claim?')) return;
    try {
      await api.delete(`/api/admin/marketing/claims/${id}`);
      setClaims(claims.filter((c) => c.id !== id));
      toast.success('Claim removed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const applyLock = async () => {
    if (!lockForm.objective) {
      toast.error('Pick an objective');
      return;
    }
    try {
      const { data } = await api.post('/api/admin/marketing/lock', lockForm);
      setConfig(data);
      toast.success('Objective locked');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Lock failed');
    }
  };

  const clearLock = async () => {
    try {
      await api.delete('/api/admin/marketing/lock');
      toast.success('Lock cleared');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clear lock');
    }
  };

  if (loading) {
    return (
      <AdminSidebarLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
        </div>
      </AdminSidebarLayout>
    );
  }

  return (
    <AdminSidebarLayout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-marketing-page">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-white flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-[#00E5FF]" />
              Marketing Autopilot
            </h1>
            <p className="text-[#A0B4CC] text-sm mt-1">
              Data-driven objective + reasoning. Content generation & publishing arrive in Phase 2/3.
            </p>
          </div>
          <Button
            onClick={fetchAll} variant="ghost"
            className="text-[#00E5FF]"
            disabled={refreshing}
            data-testid="marketing-refresh-btn"
          >
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Refresh
          </Button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-[#041A3B] border border-white/10 mb-6 flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="strategy" data-testid="tab-strategy">Why This Campaign?</TabsTrigger>
            <TabsTrigger value="campaigns" data-testid="tab-campaigns">Campaigns</TabsTrigger>
            <TabsTrigger value="connections" data-testid="tab-connections">Connections</TabsTrigger>
            <TabsTrigger value="health" data-testid="tab-health">Business Health</TabsTrigger>
            <TabsTrigger value="claims" data-testid="tab-claims">Approved Claims</TabsTrigger>
            <TabsTrigger value="config" data-testid="tab-config">Settings</TabsTrigger>
          </TabsList>

          {/* ── Strategy tab ── */}
          <TabsContent value="strategy">
            <Card className="bg-gradient-to-br from-[#041A3B] to-[#0A254D] border-[#00E5FF]/20 mb-6" data-testid="strategy-card">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  Chosen Objective
                  <Badge className={
                    strategy?.confidence === 'high' ? 'bg-[#00E676]/20 text-[#00E676]'
                    : strategy?.confidence === 'medium' ? 'bg-[#FFEA00]/20 text-[#FFEA00]'
                    : strategy?.confidence === 'admin_override' ? 'bg-[#007AFF]/20 text-[#007AFF]'
                    : 'bg-amber-400/20 text-amber-400'
                  }>
                    Confidence · {strategy?.confidence || '—'}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-outfit text-2xl text-white font-semibold mb-4" data-testid="strategy-objective">
                  {strategy?.objective_label}
                </p>
                <div className="mb-4">
                  <p className="text-[10px] uppercase tracking-widest text-[#5C7599] mb-2">Reasoning</p>
                  <ul className="space-y-1.5">
                    {(strategy?.reasoning || []).map((r, i) => (
                      <li key={`${strategy.objective}-r-${i}`} className="text-[#A0B4CC] text-sm flex items-start gap-2">
                        <span className="text-[#00E5FF] mt-1">•</span> {r}
                      </li>
                    ))}
                    {(strategy?.reasoning || []).length === 0 && (
                      <li className="text-[#5C7599] text-sm italic">No reasoning captured.</li>
                    )}
                  </ul>
                </div>
                {strategy?.signals_used?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-[10px] uppercase tracking-widest text-[#5C7599] mb-2">Signals used</p>
                    <div className="flex flex-wrap gap-1.5">
                      {strategy.signals_used.map((s) => (
                        <Badge key={s} className="bg-[#00E5FF]/10 text-[#00E5FF]">{s}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {strategy?.allocation_7d && (
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-[#5C7599] mb-2">Planned 7-day content allocation</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {Object.entries(strategy.allocation_7d).map(([k, v]) => (
                        <div key={k} className="p-2 rounded-md bg-[#000B18]/60 border border-white/5">
                          <p className="text-[10px] uppercase text-[#5C7599]">{k}</p>
                          <p className="font-mono text-white font-bold">{Math.round(v * 100)}%</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {(strategy?.operational_alerts || []).length > 0 && (
              <Card className="bg-[#041A3B] border-amber-400/30 mb-6" data-testid="strategy-alerts-card">
                <CardHeader>
                  <CardTitle className="text-white text-base flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-400" />
                    Operational alerts marketing cannot fix
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {strategy.operational_alerts.map((a, i) => (
                    <div
                      key={`${a.kind}-${i}`}
                      className={`p-3 rounded-lg border ${
                        a.severity === 'high'
                          ? 'bg-[#FF1744]/10 border-[#FF1744]/30'
                          : 'bg-amber-400/10 border-amber-400/30'
                      }`}
                      data-testid={`alert-${a.kind}`}
                    >
                      <p className="text-[10px] uppercase tracking-widest mb-1"
                         style={{ color: a.severity === 'high' ? '#FF1744' : '#F59E0B' }}>
                        {a.severity} · {a.kind.replace(/_/g, ' ')}
                      </p>
                      <p className="text-[#A0B4CC] text-sm">{a.message}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            <Card className="bg-[#041A3B] border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <Lock className="w-4 h-4 text-[#00E5FF]" />
                  Lock objective for N days
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {config?.lock ? (
                  <div className="p-3 rounded-md bg-[#007AFF]/10 border border-[#007AFF]/30 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-white text-sm">
                        Locked to <span className="font-semibold">{config.lock.objective}</span> until
                        {' '}<span className="font-mono">{new Date(config.lock.expires_at).toLocaleString()}</span>
                      </p>
                    </div>
                    <Button variant="ghost" onClick={clearLock} className="text-[#FF1744]" data-testid="clear-lock-btn">
                      <LockOpen className="w-4 h-4 mr-1" /> Clear
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-2 items-end">
                    <div className="flex-1 min-w-0">
                      <Label className="text-white text-xs uppercase tracking-widest">Objective</Label>
                      <select
                        value={lockForm.objective}
                        onChange={(e) => setLockForm({ ...lockForm, objective: e.target.value })}
                        className="mt-1 w-full h-10 rounded-md bg-[#000B18] border border-white/20 text-white px-3 text-sm"
                        data-testid="lock-objective-select"
                      >
                        <option value="">Select an objective…</option>
                        {objectives.filter(o => o.key !== 'insufficient_data').map(o => (
                          <option key={o.key} value={o.key}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="w-24">
                      <Label className="text-white text-xs uppercase tracking-widest">Days</Label>
                      <Input
                        type="number" min="1" max="30" value={lockForm.days}
                        onChange={(e) => setLockForm({ ...lockForm, days: Number(e.target.value) })}
                        className="mt-1 bg-[#000B18] border-white/20 text-white"
                        data-testid="lock-days-input"
                      />
                    </div>
                    <Button onClick={applyLock} className="btn-primary" data-testid="apply-lock-btn">
                      <Lock className="w-4 h-4 mr-1" /> Lock
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Health tab ── */}
          <TabsContent value="campaigns">
            <Card className="bg-[#041A3B] border-white/10 mb-6">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-white text-base flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#00E5FF]" />
                    Content Drafts
                  </CardTitle>
                  <p className="text-[#5C7599] text-xs mt-1">
                    GPT-6 Astra caption + Nano Banana image. Nothing publishes today — Phase 3 wires Facebook + Instagram.
                  </p>
                </div>
                <Button
                  onClick={generateDraft} disabled={generating}
                  className="btn-primary"
                  data-testid="generate-draft-btn"
                >
                  {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  Generate test draft
                </Button>
              </CardHeader>
              <CardContent>
                {campaigns.length === 0 ? (
                  <p className="text-[#5C7599] text-sm text-center py-10">
                    No campaigns yet. Press <span className="text-white">Generate test draft</span> — takes ~15s.
                  </p>
                ) : (
                  <ul className="space-y-4" data-testid="campaigns-list">
                    {campaigns.map((c) => {
                      const skipped = c.status === 'skipped';
                      const content = c.content || {};
                      const imgUrl = c.image?.url;
                      const isRegen = regeneratingId === c.id;
                      return (
                        <li key={c.id} className="p-4 rounded-lg bg-[#000B18]/60 border border-white/5" data-testid={`campaign-row-${c.id}`}>
                          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className={
                                skipped ? 'bg-amber-400/20 text-amber-400'
                                  : c.status === 'draft' ? 'bg-[#00E5FF]/20 text-[#00E5FF]'
                                  : 'bg-[#00E676]/20 text-[#00E676]'
                              }>{c.status}</Badge>
                              <span className="text-white text-sm">{c.objective_label || c.objective}</span>
                              {c.confidence && (
                                <Badge className="bg-white/5 text-[#A0B4CC] text-[10px]">confidence · {c.confidence}</Badge>
                              )}
                              {(c.used_sensitive_topics || []).map((t) => (
                                <Badge key={t} className="bg-[#FF1744]/10 text-[#FF1744] text-[10px]">{t}</Badge>
                              ))}
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost" size="sm" className="text-[#00E5FF] h-8 px-2"
                                onClick={() => regeneratePart(c.id, 'content')}
                                disabled={isRegen || skipped}
                                data-testid={`campaign-regen-content-${c.id}`}
                              >
                                {isRegen ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Regen caption'}
                              </Button>
                              <Button
                                variant="ghost" size="sm" className="text-[#00E5FF] h-8 px-2"
                                onClick={() => regeneratePart(c.id, 'image')}
                                disabled={isRegen || skipped}
                                data-testid={`campaign-regen-image-${c.id}`}
                              >
                                Regen image
                              </Button>
                              {!skipped && (
                                <Button
                                  size="sm"
                                  className="bg-[#00E676] text-[#001627] hover:bg-[#00C853] h-8 px-2"
                                  onClick={() => publishCampaign(c.id)}
                                  disabled={publishingId === c.id}
                                  data-testid={`campaign-publish-${c.id}`}
                                >
                                  {publishingId === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Publish'}
                                </Button>
                              )}
                              <Button
                                variant="ghost" size="sm" className="text-[#FF1744] h-8 px-2"
                                onClick={() => deleteCampaign(c.id)}
                                data-testid={`campaign-delete-${c.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                          {skipped ? (
                            <p className="text-amber-400 text-sm">
                              Skipped: {c.reason}
                            </p>
                          ) : (
                            <div className="grid md:grid-cols-[220px_1fr] gap-4">
                              <div>
                                {imgUrl && !brokenImgs[imgUrl] ? (
                                  <img
                                    src={imgUrl.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${imgUrl}` : imgUrl}
                                    alt=""
                                    onError={() => setBrokenImgs((b) => ({ ...b, [imgUrl]: true }))}
                                    className="w-full aspect-square rounded-md object-cover border border-white/10"
                                    data-testid={`campaign-image-${c.id}`}
                                  />
                                ) : brokenImgs[imgUrl] ? (
                                  <div className="w-full aspect-square rounded-md bg-[#000B18] border border-white/10 flex flex-col items-center justify-center gap-2 text-[#5C7599] text-xs text-center px-3" data-testid={`campaign-image-missing-${c.id}`}>
                                    Image file is missing. Click "Regen image" to create a new one.
                                  </div>
                                ) : (
                                  <div className="w-full aspect-square rounded-md bg-[#000B18] border border-white/10 flex items-center justify-center text-[#5C7599] text-xs text-center px-3">
                                    {c.image?.reason || 'No image'}
                                  </div>
                                )}
                              </div>
                              <div className="space-y-2 min-w-0">
                                {['caption_en', 'caption_fil', 'caption_taglish'].map((k) => (
                                  content[k] && (
                                    <div key={k}>
                                      <p className="text-[9px] uppercase tracking-widest text-[#5C7599]">{k.replace('caption_', '')}</p>
                                      <p className="text-white text-sm break-words">{content[k]}</p>
                                    </div>
                                  )
                                ))}
                                {(content.hashtags || []).length > 0 && (
                                  <p className="text-[#00E5FF] text-xs break-words">
                                    {content.hashtags.map((h) => `#${h}`).join(' ')}
                                  </p>
                                )}
                                <div className="flex flex-wrap gap-x-3 text-[11px] text-[#A0B4CC]">
                                  {content.call_to_action && <span>CTA: <span className="text-white">{content.call_to_action}</span></span>}
                                  {content.destination_url && (
                                    <a href={content.destination_url} target="_blank" rel="noreferrer" className="text-[#00E5FF] underline truncate max-w-[300px]">
                                      {content.destination_url}
                                    </a>
                                  )}
                                </div>
                                {content.disclaimer && (
                                  <p className="text-[10px] italic text-[#5C7599]">Disclaimer: {content.disclaimer}</p>
                                )}
                              </div>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Connections tab ── */}
          <TabsContent value="connections">
            {(!liveMode.facebook_page && !liveMode.instagram_business) && (
              <div className="mb-4 p-3 rounded-lg bg-amber-400/10 border border-amber-400/30 flex items-start gap-2" data-testid="mock-mode-banner">
                <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="text-[13px] text-amber-100 leading-relaxed">
                  <p className="font-semibold text-amber-400 mb-0.5">MOCK MODE</p>
                  <p>Meta credentials are not configured on the server. Any publish will simulate a successful post and stamp <span className="font-mono text-[11px]">mock=true</span> on the ledger. Set <span className="font-mono text-[11px]">META_APP_ID</span> + <span className="font-mono text-[11px]">META_APP_SECRET</span> in the backend .env to go live.</p>
                </div>
              </div>
            )}
            <Card className="bg-[#041A3B] border-white/10 mb-6">
              <CardHeader>
                <CardTitle className="text-white text-base">Connect a page or business account</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-white text-xs uppercase tracking-widest">Network</Label>
                    <select
                      value={accountForm.network}
                      onChange={(e) => setAccountForm({ ...accountForm, network: e.target.value })}
                      className="mt-1 w-full h-10 rounded-md bg-[#000B18] border border-white/20 text-white px-3 text-sm"
                      data-testid="account-network-select"
                    >
                      <option value="facebook_page">Facebook Page</option>
                      <option value="instagram_business">Instagram Business</option>
                      <option value="mock">Mock (safe test)</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-white text-xs uppercase tracking-widest">Page / IG-business ID</Label>
                    <Input
                      value={accountForm.external_account_id}
                      onChange={(e) => setAccountForm({ ...accountForm, external_account_id: e.target.value })}
                      placeholder="e.g. 123456789012345"
                      className="mt-1 bg-[#000B18] border-white/20 text-white font-mono"
                      data-testid="account-external-id-input"
                    />
                  </div>
                  <div>
                    <Label className="text-white text-xs uppercase tracking-widest">Display name</Label>
                    <Input
                      value={accountForm.display_name}
                      onChange={(e) => setAccountForm({ ...accountForm, display_name: e.target.value })}
                      placeholder="e.g. AGA Official"
                      className="mt-1 bg-[#000B18] border-white/20 text-white"
                      data-testid="account-display-name-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-2">
                    <Label className="text-white text-xs uppercase tracking-widest">Access token (server-side)</Label>
                    <Input
                      type="password"
                      value={accountForm.access_token}
                      onChange={(e) => setAccountForm({ ...accountForm, access_token: e.target.value })}
                      placeholder="Long-lived Page / IG Business token"
                      className="mt-1 bg-[#000B18] border-white/20 text-white font-mono"
                      data-testid="account-token-input"
                    />
                    <p className="text-[10px] text-[#5C7599] mt-1">
                      Stored server-side. Only the last 4 characters are ever shown back to the UI. Never logged.
                    </p>
                  </div>
                  <div>
                    <Label className="text-white text-xs uppercase tracking-widest">Caption language</Label>
                    <select
                      value={accountForm.language}
                      onChange={(e) => setAccountForm({ ...accountForm, language: e.target.value })}
                      className="mt-1 w-full h-10 rounded-md bg-[#000B18] border border-white/20 text-white px-3 text-sm"
                      data-testid="account-language-select"
                    >
                      <option value="en">English</option>
                      <option value="fil">Filipino</option>
                      <option value="taglish">Taglish</option>
                    </select>
                  </div>
                </div>
                <Button onClick={addAccount} disabled={savingAccount} className="btn-primary" data-testid="account-add-btn">
                  {savingAccount ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Connect account
                </Button>
              </CardContent>
            </Card>
            <Card className="bg-[#041A3B] border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-base">Connected accounts · {accounts.length}</CardTitle>
              </CardHeader>
              <CardContent>
                {accounts.length === 0 ? (
                  <p className="text-[#5C7599] text-sm text-center py-6">No connected accounts.</p>
                ) : (
                  <ul className="space-y-2" data-testid="accounts-list">
                    {accounts.map((a) => (
                      <li key={a.id} className="p-3 rounded-md bg-[#000B18]/60 border border-white/5 flex flex-wrap items-center justify-between gap-3" data-testid={`account-row-${a.id}`}>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-white font-medium truncate">{a.display_name}</p>
                            <Badge className="bg-[#00E5FF]/10 text-[#00E5FF] text-[10px]">{a.network}</Badge>
                            <Badge className="bg-white/5 text-[#A0B4CC] text-[10px]">{a.language}</Badge>
                            {liveMode[a.network] ? (
                              <Badge className="bg-[#00E676]/20 text-[#00E676] text-[10px]">LIVE</Badge>
                            ) : (
                              <Badge className="bg-amber-400/20 text-amber-400 text-[10px]">MOCK</Badge>
                            )}
                            {!a.enabled && (
                              <Badge className="bg-[#5C7599]/20 text-[#5C7599] text-[10px]">paused</Badge>
                            )}
                          </div>
                          <p className="text-[#5C7599] text-[11px] font-mono mt-0.5">
                            id · {a.external_account_id}
                            {a.token_configured && <> · token <span className="text-[#A0B4CC]">{a.token_tail}</span></>}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => toggleAccount(a.id, !a.enabled)}
                            className={a.enabled ? 'text-amber-400 h-8 px-2' : 'text-[#00E676] h-8 px-2'}
                            data-testid={`account-toggle-${a.id}`}
                          >
                            {a.enabled ? 'Pause' : 'Enable'}
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => deleteAccount(a.id)}
                            className="text-[#FF1744] h-8 px-2"
                            data-testid={`account-delete-${a.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Health tab ── */}
          <TabsContent value="health">
            <Card className="bg-[#041A3B] border-white/10 mb-6">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <Activity className="w-4 h-4 text-[#00E5FF]" />
                  Business Health · rolling {health?.window_days || 14} days
                </CardTitle>
                <span className="text-[10px] uppercase tracking-widest text-[#5C7599]">
                  As of {health?.generated_at ? new Date(health.generated_at).toLocaleString() : '—'}
                </span>
              </CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(health?.signals || {}).map(([key, sig]) => (
                  <div key={key} className="p-3 rounded-lg bg-[#000B18]/60 border border-white/5" data-testid={`signal-${key}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-white font-medium text-sm">{key.replace(/_/g, ' ')}</p>
                      {sig.sufficient_data ? (
                        <Badge className="bg-[#00E676]/20 text-[#00E676] text-[10px]">
                          <CheckCircle className="w-3 h-3 mr-1" /> sufficient data
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-400/20 text-amber-400 text-[10px]">
                          <AlertTriangle className="w-3 h-3 mr-1" /> insufficient data
                        </Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {Object.entries(sig).filter(([k]) => k !== 'sufficient_data').map(([k, v]) => (
                        <div key={k} className="text-[11px]">
                          <p className="text-[#5C7599] uppercase tracking-wider">{k.replace(/_/g, ' ')}</p>
                          <p className="font-mono text-white">
                            {typeof v === 'number' ? (v % 1 === 0 ? v : v.toFixed(3)) : String(v)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Claims tab ── */}
          <TabsContent value="claims">
            <Card className="bg-[#041A3B] border-white/10 mb-6">
              <CardHeader>
                <CardTitle className="text-white text-base">Add approved claim</CardTitle>
                <p className="text-[#5C7599] text-xs mt-1">
                  Marketing content about stock/participation value, referral earnings, insurance, or withdrawals
                  MUST use language from this library — with a cited source and validity date.
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-white text-xs uppercase tracking-widest">Claim text</Label>
                  <Textarea
                    value={claimForm.text}
                    onChange={(e) => setClaimForm({ ...claimForm, text: e.target.value })}
                    placeholder="e.g. Members can request a withdrawal of accrued referral commissions monthly, subject to processing terms."
                    className="mt-1 bg-[#000B18] border-white/20 text-white min-h-[80px]"
                    data-testid="claim-text-input"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-white text-xs uppercase tracking-widest">Topic</Label>
                    <select
                      value={claimForm.topic}
                      onChange={(e) => setClaimForm({ ...claimForm, topic: e.target.value })}
                      className="mt-1 w-full h-10 rounded-md bg-[#000B18] border border-white/20 text-white px-3 text-sm"
                      data-testid="claim-topic-select"
                    >
                      <option value="general">General</option>
                      <option value="referrals">Referral earnings</option>
                      <option value="stocks">Stock / participation value</option>
                      <option value="insurance">Insurance benefits</option>
                      <option value="withdrawals">Withdrawals</option>
                      <option value="discounts">Merchant discounts</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-white text-xs uppercase tracking-widest">Source</Label>
                    <Input
                      value={claimForm.source}
                      onChange={(e) => setClaimForm({ ...claimForm, source: e.target.value })}
                      placeholder="e.g. Terms of Use §3.1"
                      className="mt-1 bg-[#000B18] border-white/20 text-white"
                      data-testid="claim-source-input"
                    />
                  </div>
                  <div>
                    <Label className="text-white text-xs uppercase tracking-widest">Valid until</Label>
                    <Input
                      type="date" value={claimForm.valid_until}
                      onChange={(e) => setClaimForm({ ...claimForm, valid_until: e.target.value })}
                      className="mt-1 bg-[#000B18] border-white/20 text-white"
                      data-testid="claim-valid-until-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-white text-xs uppercase tracking-widest">Qualifications / caveats</Label>
                  <Textarea
                    value={claimForm.qualifications}
                    onChange={(e) => setClaimForm({ ...claimForm, qualifications: e.target.value })}
                    placeholder="e.g. Subject to a 30-day holding period. Processing fees apply."
                    className="mt-1 bg-[#000B18] border-white/20 text-white min-h-[60px]"
                    data-testid="claim-qualifications-input"
                  />
                </div>
                <Button onClick={addClaim} disabled={addingClaim} className="btn-primary" data-testid="claim-add-btn">
                  {addingClaim ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Add claim
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-[#041A3B] border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-base">Library · {claims.length} claim{claims.length === 1 ? '' : 's'}</CardTitle>
              </CardHeader>
              <CardContent>
                {claims.length === 0 ? (
                  <p className="text-[#5C7599] text-sm text-center py-6">
                    No approved claims yet. Content about earnings / insurance / withdrawals will be skipped until at least one claim is added.
                  </p>
                ) : (
                  <ul className="space-y-3" data-testid="claims-list">
                    {claims.map((c) => (
                      <li key={c.id} className="p-3 rounded-lg bg-[#000B18]/60 border border-white/5" data-testid={`claim-row-${c.id}`}>
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <Badge className="bg-[#00E5FF]/10 text-[#00E5FF] text-[10px]">{c.topic}</Badge>
                          <Button
                            variant="ghost" size="sm"
                            className="text-[#FF1744] h-7 px-2"
                            onClick={() => deleteClaim(c.id)}
                            data-testid={`claim-delete-btn-${c.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                        <p className="text-white text-sm mb-1.5">{c.text}</p>
                        <p className="text-[#5C7599] text-[11px]">
                          Source: <span className="text-[#A0B4CC]">{c.source}</span>
                          {c.valid_until && <> · Valid until <span className="text-[#A0B4CC]">{c.valid_until}</span></>}
                        </p>
                        {c.qualifications && (
                          <p className="text-[10px] italic text-[#5C7599] mt-1">Qualifications: {c.qualifications}</p>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Config tab ── */}
          <TabsContent value="config">
            <Card className="bg-[#041A3B] border-white/10 mb-6">
              <CardHeader>
                <CardTitle className="text-white text-base">Analysis window + starter strategy</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-white text-xs uppercase tracking-widest">Rolling analysis window (days)</Label>
                    <Input
                      type="number" min="3" max="90" value={config?.window_days || 14}
                      onChange={(e) => setConfig({ ...config, window_days: Number(e.target.value) })}
                      className="mt-1 bg-[#000B18] border-white/20 text-white"
                      data-testid="config-window-days"
                    />
                  </div>
                  <div>
                    <Label className="text-white text-xs uppercase tracking-widest">Starter strategy (when data is insufficient)</Label>
                    <select
                      value={config?.starter_strategy || 'member_acquisition'}
                      onChange={(e) => setConfig({ ...config, starter_strategy: e.target.value })}
                      className="mt-1 w-full h-10 rounded-md bg-[#000B18] border border-white/20 text-white px-3 text-sm"
                      data-testid="config-starter-select"
                    >
                      {objectives.filter(o => o.key !== 'insufficient_data').map(o => (
                        <option key={o.key} value={o.key}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <Button
                  onClick={() => saveConfig({
                    window_days: config.window_days,
                    starter_strategy: config.starter_strategy,
                  })}
                  disabled={savingConfig}
                  className="btn-primary"
                  data-testid="config-save-window-btn"
                >
                  {savingConfig ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save window + starter
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-[#041A3B] border-white/10 mb-6">
              <CardHeader>
                <CardTitle className="text-white text-base">Brand facts (used at content-generation time)</CardTitle>
                <p className="text-[#5C7599] text-xs mt-1">
                  These are the only brand statements the AI is allowed to echo verbatim. Keep them factual and current.
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                {['product_name', 'tagline', 'primary_cta_url', 'merchant_cta_url'].map((k) => (
                  <div key={k}>
                    <Label className="text-white text-xs uppercase tracking-widest">{k.replace(/_/g, ' ')}</Label>
                    <Input
                      value={config?.brand_facts?.[k] || ''}
                      onChange={(e) => setConfig({
                        ...config,
                        brand_facts: { ...(config?.brand_facts || {}), [k]: e.target.value },
                      })}
                      className="mt-1 bg-[#000B18] border-white/20 text-white"
                      data-testid={`config-brand-${k}`}
                    />
                  </div>
                ))}
                <Button
                  onClick={() => saveConfig({ brand_facts: config.brand_facts })}
                  disabled={savingConfig}
                  className="btn-primary"
                  data-testid="config-save-brand-btn"
                >
                  {savingConfig ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save brand facts
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-[#041A3B] border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-base">Scoring thresholds</CardTitle>
                <p className="text-[#5C7599] text-xs mt-1">
                  The strategy engine's transparent knobs. Change these to reflect your priorities.
                </p>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  ['merchant_coverage_min_ratio', 'Coverage floor (merchants running an active offer)'],
                  ['zero_result_search_rate_max', 'Max zero-result search rate'],
                  ['conversion_floor', 'Registration→paid conversion floor'],
                  ['dormancy_ceiling', 'Dormancy ceiling'],
                  ['redemption_rate_floor', 'Redemption success floor'],
                ].map(([k, label]) => (
                  <div key={k}>
                    <Label className="text-white text-xs uppercase tracking-widest">{label}</Label>
                    <Input
                      type="number" step="0.05" min="0" max="1"
                      value={config?.rules?.[k] ?? 0}
                      onChange={(e) => setConfig({
                        ...config,
                        rules: { ...(config?.rules || {}), [k]: Number(e.target.value) },
                      })}
                      className="mt-1 bg-[#000B18] border-white/20 text-white"
                      data-testid={`config-rule-${k}`}
                    />
                  </div>
                ))}
                <div className="md:col-span-2">
                  <Button
                    onClick={() => saveConfig({ rules: config.rules })}
                    disabled={savingConfig}
                    className="btn-primary"
                    data-testid="config-save-rules-btn"
                  >
                    {savingConfig ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Save thresholds
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminSidebarLayout>
  );
}

export default AdminMarketingAutopilotPage;
